/**
 * ProShip Direct v2 — product page, plan activation and deactivation.
 *
 * Replaces the mode-card home of the reviewed build. Two differences worth keeping in mind:
 * no shipment statistics (a merchant who activated this morning has none — the comparison lives
 * in ProShip Direct Performance), and only credentials are treated as blocking, because every
 * other configuration falls back to a ProShip default.
 */
import React, { useState } from 'react';
import { Box, Grid, Typography, Button, Alert, Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';

import withLayout from '../../../hocs/Layout';
import PageTitle from '../../../common/PageTitle';
import PageHero from '../../../common/PageHero';
import ConfirmDialog from '../../../common/ConfirmDialog';
import {
  heroTitleSx, heroSubSx,
  pageContainerSx, sectionTitleSx, primaryBtnSx, secondaryBtnSx,
} from '../../../styles/patterns';
import { statCardSx, statLabelSx, statValueSx } from '../components/stats';
import { NAVY, SUBTLE, BORDER, WHITE, LIGHT_BG, SUCCESS, WARNING, ERROR, PRIMARY_BLUE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { formatDate } from '../../../helpers/UtilityHelper';

import {
  usePlan, useDirectOffers, useDirectAccounts, useCourierAvailability, useMerchantBilling,
  useDirectOpportunity,
  countsAgainstPlan, ownConfigCount, OWNER, ACTIVITY_ENTITY,
} from '../data/hooks';
import { SETUP_DIMENSIONS, CREDENTIAL_STATE } from '../data/defaults';
import { dpPath, SETTINGS_PATHS } from '../flags';
import { CredentialChip, SetupSymbols } from '../components/chips';
import { MONO } from '../components/directCellStyles';
import DirectMarketing from '../components/DirectMarketing';
import OffersInbox from '../components/OffersInbox';
import PlanActivationModal from '../components/PlanActivationModal';
import PlanUsageCard from '../components/PlanUsageCard';
import PlanChangeModal from '../components/PlanChangeModal';
import PaymentOtpModal from '../components/PaymentOtpModal';
import ActivityLogModal from '../components/ActivityLogModal';

const bodySx = { fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.5 };
const smallSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE };
const strongSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };

/**
 * One Direct account, as a status row: is the integration live, and how much of the setup is the
 * merchant's own. No per-row action — a Configure button on every row was four buttons doing the
 * same thing, so there is one in the header instead.
 */
function CourierRow({ account }) {
  const blocked = account.credentials !== CREDENTIAL_STATE.TESTED;

  return (
    /*
      A grid, not a flex row. Flexed, every column started wherever the previous one's text ended,
      so the single account reading "integration incomplete" pushed its whole run of symbols right
      of the three reading "integration live" — the columns only looked aligned while the copy
      happened to be the same length. Fixed tracks align them whatever the text does.
    */
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '190px 200px minmax(300px, 1fr) 92px' },
        alignItems: 'center',
        columnGap: GAP.md,
        rowGap: 0.75,
        px: 1.25,
        py: 0.85,
        borderTop: `1px solid ${BORDER}`,
        transition: 'background-color 120ms',
        '&:hover': { bgcolor: LIGHT_BG },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={strongSx}>{account.carrierName}</Typography>
        <Typography sx={{ ...smallSx, fontFamily: MONO }}>{account.accountCode}</Typography>
      </Box>

      {/*
        Integration status — can ProShip talk to this account at all. The biggest pendency of the
        lot when it is failing, so it links to My Couriers, where the credentials can be fixed.
      */}
      {blocked ? (
        <Box
          component={Link}
          to={dpPath('/couriers')}
          sx={{
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: GAP.sm,
            textDecoration: 'none',
            '&:hover .dp-integration-label': { color: PRIMARY_BLUE, textDecoration: 'underline' },
          }}
        >
          <CredentialChip state={account.credentials} />
          <Tooltip title='Click to fix the credentials for this account'>
            <Typography className='dp-integration-label' sx={smallSx}>
              integration incomplete
            </Typography>
          </Tooltip>
        </Box>
      ) : (
        <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: GAP.sm }}>
          <CredentialChip state={account.credentials} />
          <Typography sx={smallSx}>integration live</Typography>
        </Box>
      )}

      {/*
        Setup status — which of the five are theirs rather than ours, named. No count underneath:
        the card header already totals it across accounts, and the symbols say it per row.
      */}
      <Box sx={{ minWidth: 0, px: { md: 1 } }}>
        <SetupSymbols
          config={account.config}
          dimensions={SETUP_DIMENSIONS}
          labelled
          spread
          linkTo={(d) => `${dpPath('/courier-setup')}?account=${account.id}&tab=${d.route}`}
        />
      </Box>

      <Typography
        sx={{
          ...smallSx,
          minWidth: 0,
          textAlign: { xs: 'left', md: 'right' },
          color: blocked ? WARNING : account.enabled ? SUCCESS : SUBTLE,
        }}
      >
        {blocked ? 'Cannot ship' : account.enabled ? 'Shipping' : 'Switched off'}
      </Typography>
    </Box>
  );
}

/** Indian grouping, and an em dash rather than "0" for a figure that could not be read. */
const nfmt = (value) =>
  Number.isFinite(Number(value)) && value !== null ? Number(value).toLocaleString('en-IN') : '—';

/**
 * One stat in the row at the top.
 *
 * Three things it does beyond showing a number: carries an accent stripe when something wants
 * attention, becomes a link when there is somewhere to act on it, and holds the detail in a
 * tooltip. The tooltip is why these findings fit here at all — the caveat on a number like
 * "unprocessed shipments that failed on coverage" is a sentence long and would otherwise need a
 * card of its own.
 */
function StatCard({ stat }) {
  const inner = (
    <Box
      sx={{
        ...statCardSx,
        height: '100%',
        borderLeft: stat.accent ? `3px solid ${stat.accent}` : undefined,
        ...(stat.to
          ? {
              cursor: 'pointer',
              transition: 'border-color 120ms, box-shadow 120ms',
              '&:hover': { borderColor: stat.accent || PRIMARY_BLUE },
            }
          : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
        <Typography sx={statLabelSx}>{stat.label}</Typography>
        {stat.tooltip && <InfoOutlinedIcon sx={{ fontSize: 12, color: SUBTLE, flexShrink: 0 }} />}
      </Box>
      <Typography sx={{ ...statValueSx, fontVariantNumeric: 'tabular-nums', color: stat.accent || NAVY }}>
        {stat.value}
      </Typography>
      <Typography sx={smallSx}>{stat.sub}</Typography>
    </Box>
  );

  const body = stat.to ? (
    <Box component={Link} to={stat.to} sx={{ textDecoration: 'none', display: 'block', height: '100%' }}>
      {inner}
    </Box>
  ) : (
    inner
  );

  // The icon marks that there is more to read; the whole card is the target, because a 12px icon
  // is not one.
  return stat.tooltip ? (
    <Tooltip title={stat.tooltip} placement='bottom-start'>
      <Box sx={{ height: '100%' }}>{body}</Box>
    </Tooltip>
  ) : (
    body
  );
}

/** Where to go next, with the reason rather than just the label. */
function NextStep({ icon, title, body, to, cta }) {
  return (
    <Box
      sx={{
        p: 1.5,
        height: '100%',
        bgcolor: WHITE,
        border: `1px solid ${BORDER}`,
        borderRadius: RADIUS.md,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        '& > svg': { fontSize: 20, color: PRIMARY_BLUE },
      }}
    >
      {icon}
      <Typography sx={strongSx}>{title}</Typography>
      <Typography sx={{ ...smallSx, flex: 1 }}>{body}</Typography>
      <Button component={Link} to={to} size='small' sx={{ ...secondaryBtnSx, alignSelf: 'flex-start', mt: 0.5 }}>
        {cta}
      </Button>
    </Box>
  );
}

function Active({ plan, usage, couriers, couriersLoading, availability, opportunity, onReset, onDeactivate }) {
  const blocked = couriers.filter((c) => c.credentials !== CREDENTIAL_STATE.TESTED);
  const supplied = couriers.reduce((sum, c) => sum + ownConfigCount(c), 0);
  const shipping = couriers.filter((c) => c.enabled && c.credentials === CREDENTIAL_STATE.TESTED).length;
  const enabledProship = availability.rows.filter((r) => r.owner === OWNER.PROSHIP && r.enabled).length;
  const possible = couriers.length * SETUP_DIMENSIONS.length;

  // Plan status, price and renewal all live in PlanUsageCard above — repeating them here would
  // be the same fact twice on one screen.
  /*
    NOT "no couriers" until we have actually asked. The list is a fetch now, so an empty array on
    the first render means "still loading" as often as it means "none" — and rendering "Connect
    your first courier" to a merchant who has four of them, for the half second before the
    response lands, is worse than rendering nothing.
  */
  const noCouriers = !couriersLoading && couriers.length === 0;

  // Against the plan's allowance, not against itself: "2 of 3" tells a merchant how much of what
  // they are paying for is in use, which "2 of 2" never could.
  const allowed = usage.plan?.courierIntegrations || 0;
  /* Against the plan's own rule — switched-off and retired couriers cost nothing. See countsAgainstPlan. */
  const counted = couriers.filter(countsAgainstPlan).length;

  /*
    WHAT ProShip'S NETWORK BEING OFF IS COSTING, in the same row as everything else.

    These were three full-width cards above the plan card, which was more room than a finding on a
    pass-through page deserves — each carried a paragraph that a hover carries just as well. They
    appear only while some of ProShip's couriers are switched off; with all of them on there is
    nothing to be missing.
  */
  const opportunityStatCards = opportunity.stats.map((s) => ({
    ...s,
    accent: { INFO: PRIMARY_BLUE, WARN: WARNING }[s.tone],
    to: s.absolute ? s.to : dpPath(s.to),
  }));

  const baseStats = [
    {
      label: 'Your courier contracts',
      value: `${counted} / ${allowed}`,
      sub: noCouriers
        ? `${allowed} included in your ${usage.plan?.name || 'Direct'} plan`
        : blocked.length
          ? `${shipping} shipping · ${blocked.length} blocked on credentials`
          : `all ${shipping} shipping`,
      accent: noCouriers ? undefined : blocked.length ? WARNING : SUCCESS,
    },
    {
      label: 'ProShip couriers available',
      value: availability.isLoading ? '—' : String(enabledProship),
      sub: noCouriers ? 'carrying everything for now' : 'carrying everything else',
    },
    {
      label: 'Activated',
      value: plan.activatedAt ? formatDate(plan.activatedAt, 'dd MMM') : '—',
      sub: plan.activatedBy || '',
      /*
        First to go when the row fills up. The activation date is the least actionable thing here —
        it never changes and it asks nothing of the merchant — and it is on the plan card below in
        full. Anything about couriers that cannot ship earns the space ahead of it.
      */
      droppable: true,
    },
  ];

  const stats = [
    ...baseStats.filter((s) => !(s.droppable && opportunityStatCards.length > 0)),
    ...opportunityStatCards,
  ];

  return (
    <>
      {/*
        auto-fit rather than a fixed md={3}: the count varies from three to five depending on what
        is switched off, and four fixed columns leave a lone fifth card stranded on its own row.
      */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(auto-fit, minmax(190px, 1fr))' },
          gap: 1.25,
        }}
      >
        {stats.map((s) => (
          <StatCard key={s.label} stat={s} />
        ))}
      </Box>

      {blocked.length > 0 && (
        <Alert
          severity='warning'
          sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.25 }}
          action={
            <Button component={Link} to={dpPath('/couriers')} size='small' sx={secondaryBtnSx}>
              Fix now
            </Button>
          }
        >
          {blocked.length === 1
            ? `${blocked[0].carrierName} cannot ship until its credentials pass the connection test.`
            : `${blocked.length} accounts cannot ship until their credentials pass the connection test.`}
        </Alert>
      )}

      {/* Your fleet, at a glance — the thing this page exists to tell you. */}
      <Box sx={{ bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: RADIUS.md, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: GAP.sm,
            px: 1.25,
            py: 1,
            flexWrap: 'wrap',
            bgcolor: LIGHT_BG,
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          <LocalShippingOutlinedIcon sx={{ fontSize: 18, color: PRIMARY_BLUE }} />
          <Typography sx={{ ...sectionTitleSx, mb: 0 }}>Your couriers</Typography>
          {/* "0 of 0 settings supplied" on a fresh account reads as a problem. It is not one. */}
          {!noCouriers && (
            <Typography sx={smallSx}>
              {supplied} of {possible} settings supplied — the rest run on ProShip defaults
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          {!noCouriers && (
            <>
              <Button
                component={Link}
                to={dpPath('/couriers/connect')}
                variant='contained'
                size='small'
                startIcon={<AddOutlinedIcon sx={{ fontSize: 16 }} />}
                sx={primaryBtnSx}
              >
                Add courier
              </Button>
              {/* One Configure for the whole list — the courier list is where every setting is. */}
              <Button
                component={Link}
                to={dpPath('/couriers')}
                size='small'
                startIcon={<TuneOutlinedIcon sx={{ fontSize: 16 }} />}
                sx={secondaryBtnSx}
              >
                Configure
              </Button>
            </>
          )}
        </Box>

        {noCouriers ? (
          /*
            Fresh activation. The plan is paid for and nothing is connected yet, so this is the
            only thing the merchant needs to do — it gets the whole card rather than one grey line
            under a header full of buttons they cannot use yet.
          */
          <Box
            sx={{
              px: 2,
              py: 3,
              borderTop: `1px solid ${BORDER}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 0.75,
            }}
          >
            <LocalShippingOutlinedIcon sx={{ fontSize: 30, color: SUBTLE }} />
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.semibold, color: NAVY }}>
              Connect your first courier
            </Typography>
            <Typography sx={{ ...smallSx, maxWidth: 460 }}>
              Add the courier account you have a contract with and enter its API credentials. Once
              the connection test passes it can carry orders the same day — serviceability, zones,
              TAT, rates and status mapping all start on ProShip defaults and you replace them
              whenever you want to.
            </Typography>
            <Button
              component={Link}
              to={dpPath('/couriers/connect')}
              variant='contained'
              size='small'
              startIcon={<AddOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{ ...primaryBtnSx, mt: 0.75 }}
            >
              Connect a courier
            </Button>
            <Typography sx={{ ...smallSx, mt: 0.5 }}>
              Until then, your ProShip couriers carry everything as before.
            </Typography>
          </Box>
        ) : (
          couriers.map((account) => <CourierRow key={account.id} account={account} />)
        )}
        {couriersLoading && couriers.length === 0 && (
          <Typography sx={{ ...smallSx, px: 1.25, py: 1.5 }}>Loading your couriers…</Typography>
        )}
      </Box>

      <Box>
        <Typography sx={{ ...sectionTitleSx, mb: 1 }}>What to do next</Typography>
        <Grid container spacing={1.25}>
          <Grid item xs={12} md={4}>
            {/* Telling a merchant with no couriers to configure one is a dead end. */}
            {noCouriers ? (
              <NextStep
                icon={<AddOutlinedIcon />}
                title='Bring your courier contract'
                body='Your carrier account and its API credentials are all that is needed. Everything else has a ProShip default behind it.'
                to={dpPath('/couriers/connect')}
                cta='Connect a courier'
              />
            ) : (
              <NextStep
                icon={<TuneOutlinedIcon />}
                title='Replace a ProShip default'
                body='Upload your own serviceability, zones, TAT or rate card for an account — each one you supply stops using ours.'
                to={dpPath('/courier-setup')}
                cta='Courier setup'
              />
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <NextStep
              icon={<RuleOutlinedIcon />}
              title='Choose which courier ships what'
              body='Send COD, heavy parcels or a particular zone to the courier you want. Without a rule, PACE picks for every order.'
              to={SETTINGS_PATHS.rules}
              cta='Courier rules'
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <NextStep
              icon={<InsightsOutlinedIcon />}
              title='Compare cost and delivery'
              body='What your couriers cost against ProShip’s, with RTO, transit time and how often an order moved back to us.'
              to={dpPath('/performance')}
              cta='Performance'
            />
          </Grid>

          {/*
            THE SECOND ROW IS WHAT THE BUYER SEES, and it is the half of Direct a merchant
            forgets. Bringing your own courier changes the label the parcel carries and the page
            the buyer opens to track it — both revert to a ProShip default nobody chose. Neither
            is reachable from this page otherwise; both are buried in Settings.

            The third is not a Direct feature at all. It is the panel's own VAS page —
            /communication-config, which carries NDR automation, WhatsApp order and address
            verification, and customer notifications. A merchant who has just moved onto their own
            contracts has every reason to revisit it, so the card links out rather than
            pretending the feature lives in Direct.
          */}
          <Grid item xs={12} md={4}>
            <NextStep
              icon={<PaletteOutlinedIcon />}
              title='Brand the tracking page'
              body='Buyers open tracking four to six times an order. Put your logo, subdomain and banners on it instead of ProShip’s.'
              to={dpPath('/tracking-page')}
              cta='Tracking page'
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <NextStep
              icon={<LabelOutlinedIcon />}
              title='Set up your label'
              body='Label size and what prints on it — your brand, the invoice number, the order date. One setting per sales channel.'
              to={SETTINGS_PATHS.labelConfig}
              cta='Label configuration'
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <NextStep
              icon={<ChatOutlinedIcon />}
              title='Value added services'
              body='NDR automation, WhatsApp order and address verification, and buyer notifications — all on the panel’s VAS page.'
              to='/communication-config'
              cta='Open in Settings'
            />
          </Grid>
        </Grid>
      </Box>

      {/*
        Deactivating is the one destructive action on this page, so it gets its own row with the
        consequence stated beside it — not a pair of stacked buttons floating to the right of a
        paragraph, which is what made this box read as an afterthought.
      */}
      <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: RADIUS.md, bgcolor: WHITE }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: GAP.md,
            px: 1.75,
            py: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <PowerSettingsNewOutlinedIcon sx={{ fontSize: 20, color: ERROR, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography sx={strongSx}>Deactivate ProShip Direct</Typography>
            <Typography sx={smallSx}>
              Your Direct couriers stop being allocated from the next order. Accounts, uploads and
              rules are kept, and ProShip couriers carry everything meanwhile.
            </Typography>
          </Box>
          <Button
            size='small'
            variant='outlined'
            onClick={onDeactivate}
            sx={{
              ...secondaryBtnSx,
              color: ERROR,
              borderColor: ERROR,
              flexShrink: 0,
              '&:hover': { borderColor: ERROR, bgcolor: `${ERROR}0A` },
            }}
          >
            Deactivate
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: GAP.sm,
            px: 1.75,
            py: 1,
            borderTop: `1px solid ${BORDER}`,
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={smallSx}>
            Accounts and figures on this build are sample data held in your browser.
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            size='small'
            onClick={onReset}
            startIcon={<RestartAltOutlinedIcon sx={{ fontSize: 14 }} />}
            sx={{ ...secondaryBtnSx, borderColor: 'transparent', color: SUBTLE }}
          >
            Restore sample data
          </Button>
        </Box>
      </Box>
    </>
  );
}

function DirectPlanHome() {
  const { plan, usage, catalog, loading: planLoading, error: planError, reload: reloadPlan, activate, deactivate, changePlan, setAutoRenew, quoteUpgrade, setRenewalPlan, clearRenewalPlan, renewNow, resetAll } = usePlan();
  /*
    Offers sit above the price list: a merchant quoted a negotiated price should not have to
    find it underneath the list-price cards. Renders nothing when there are none.
  */
  const offers = useDirectOffers();
  /*
    THE REAL ACCOUNTS, not the local fixture this page shipped against.

    `credentials` is now a state the server stored when the courier was registered (registration
    re-runs the connection probe and records the outcome) or when the merchant last retested, so
    the "cannot ship" banner reflects a carrier's actual answer rather than a seeded row.

    The four setup symbols still read the per-dimension `config`, which the adapter reports as
    unsupplied for every account because those uploads are the next piece of work. They stay on
    screen deliberately — the columns are the "what to do next" story, and each lights up as its
    upload lands.
  */
  const { couriers, loading: couriersLoading } = useDirectAccounts();
  const availability = useCourierAvailability();
  const billing = useMerchantBilling();

  const [choosingPlan, setChoosingPlan] = useState(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [confirmDisableProship, setConfirmDisableProship] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [payingRenewal, setPayingRenewal] = useState(false);

  const enabledDirect = couriers.filter((c) => c.enabled).length;
  const proshipRows = availability.rows.filter((r) => r.owner === OWNER.PROSHIP);
  const proshipEnabled = proshipRows.filter((r) => r.enabled).length;

  // Fetched once here and handed to <Active/>, which shows the same findings as stat cards.
  // Two calls to the hook would be two round trips for one set of numbers.
  const opportunity = useDirectOpportunity();

  /*
    WHAT THE HERO COUNTS: shipments, not couriers.

    It used to carry courier counts — yours, shipping now, ProShip's on — and all three are on
    the cards below, where "3 / 5" says more than "3" did. Volume is what a merchant opens this
    page wanting, and it was nowhere on it.

    The second and third appear only while ProShip's network is not fully live, because that is
    the only state in which they mean anything: with every ProShip courier carrying, unprocessed
    shipments are not a network problem and "could have carried" has nothing to point at.
  */
  const { unprocessed, shipments } = opportunity;

  const coverageEstimate =
    unprocessed.sampled && unprocessed.inspected > 0
      ? // Read from a sample, so it is an estimate and says so with a tilde. Extrapolating the
        // share is more use than reporting the sample's raw count as though it were the whole.
        Math.round((unprocessed.coverage / unprocessed.inspected) * unprocessed.total)
      : unprocessed.coverage;

  const heroStats = [
    {
      k: 'Total shipments',
      v: shipments.loading ? '—' : nfmt(shipments.total),
      title: 'Every shipment on this account, across your own couriers and ProShip’s.',
    },
    opportunity.anyProshipOff && {
      k: 'Unprocessed',
      v: unprocessed.loading ? '—' : nfmt(unprocessed.total),
      title:
        'Shipments that could not be allocated to any courier. Shown because some of ProShip’s ' +
        'couriers are switched off for this account.',
    },
    opportunity.proshipOn === 0 && {
      k: 'ProShip could have carried',
      v: unprocessed.loading
        ? '—'
        : `${unprocessed.sampled && coverageEstimate > 0 ? '~' : ''}${nfmt(coverageEstimate)}`,
      title:
        'Of those unprocessed shipments, how many failed because no courier you use covers the ' +
        'lane — the one failure a wider network absorbs. Whether ProShip serves each of those ' +
        'pincodes is a serviceability check this figure has not run, so treat it as a ceiling.' +
        (unprocessed.sampled
          ? ` Estimated from the ${nfmt(unprocessed.inspected)} most recent.`
          : ''),
    },
  ].filter(Boolean);

  return (
    <Box sx={pageContainerSx}>
      <PageTitle
        subTitle={
          plan.active
            ? 'Your courier contracts, running on ProShip'
            : 'Bring your own courier contracts onto ProShip'
        }
      >
        ProShip Direct
      </PageTitle>

      {/*
        The standard dark hero, same as Performance and Create Order. Without it this page is a
        stack of white cards on a near-white background with nothing to anchor it — and the
        inactive state already carries the hero through DirectMarketing, so an active merchant
        losing it on activation was a step backwards.

        NO SUBTITLE. It used to read "Growth plan · 10,000 of 10,000 shipments left · renews
        28 Sep", and every one of those three facts is a headline stat on the plan card
        immediately below, in more detail and with the year. Saying them twice, forty pixels
        apart, made the banner look full without telling anyone anything.

        The three stats under the title are what survive that test: none appears elsewhere here.
      */}
      {plan.active && (
        <PageHero title='ProShip Direct'>
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', gap: GAP.lg, mt: 1.5, flexWrap: 'wrap' }}>
            {heroStats.map((stat) => (
              /*
                Every hero figure carries its definition on hover. Two of the three need one:
                "unprocessed" and "could have carried" are both counts with a real caveat, and
                a hero has no room to print it without becoming a paragraph.
              */
              <Tooltip key={stat.k} title={stat.title || ''} placement='bottom-start'>
                <Box sx={{ cursor: stat.title ? 'help' : 'default' }}>
                  <Typography
                    sx={{ ...heroSubSx, fontSize: SIZE['4xs'], textTransform: 'uppercase', letterSpacing: '0.06em' }}
                  >
                    {stat.k}
                  </Typography>
                  <Typography sx={{ ...heroTitleSx, fontSize: SIZE.lg, mb: 0 }}>{stat.v}</Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>
        </PageHero>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.lg }}>
        {/*
          Surface the plan API's own answer. Without this an API failure and an empty catalog
          look identical — a page with no plan cards and no explanation — which is exactly how
          a backend problem gets mistaken for a UI one. planError carries the server's sentence
          (including "ProShip Direct is not enabled for this account", which is the master
          switch answering correctly, not a fault).
        */}
        <OffersInbox
          offers={offers.offers}
          loading={offers.loading}
          /*
            Suppressed deliberately. The only expected failure is the master switch refusing a
            merchant who is not on Direct — already shown once by planError above, and showing
            it twice (or showing an "Offers" panel to a merchant with no Direct at all) would
            be worse than silence. The hook still degrades to an empty list on any other error.
          */
          error={null}
          planActive={plan.active}
          onMarkViewed={offers.markViewed}
          onRespond={async (id, accept, reason, proof) => { await offers.respond(id, accept, reason, proof); await reloadPlan(); }}
        />
        {!plan.active && planError && (
          <Alert severity='warning'>{planError}</Alert>
        )}
        {!plan.active && !planError && !planLoading && catalog.length === 0 && (
          <Alert severity='info'>
            No plans are available to purchase right now. If you expected to see plans here, the
            plan catalog has not been set up for this environment yet.
          </Alert>
        )}
        {plan.active ? (
          <>
            <PlanUsageCard
              plan={plan}
              usage={usage}
              proship={{ enabled: proshipEnabled, total: proshipRows.length }}
              onAutoRenew={setAutoRenew}
              onToggleProship={(next) =>
                next
                  ? availability.setRowsEnabled(proshipRows, true)
                  : setConfirmDisableProship(true)
              }
              onViewLog={() => setLogOpen(true)}
              onChangePlan={() => setChangingPlan(true)}
              onRenewNow={() => setPayingRenewal(true)}
              onKeepCurrentPlan={clearRenewalPlan}
            />
            <Active
              plan={plan}
              usage={usage}
              couriers={couriers}
              couriersLoading={couriersLoading}
              availability={availability}
              opportunity={opportunity}
              onReset={resetAll}
              onDeactivate={() => setConfirmDeactivate(true)}
            />
          </>
        ) : (
          <DirectMarketing plans={catalog} onChoose={setChoosingPlan} disabled={billing.isLoading || planLoading} />
        )}

      </Box>

      {choosingPlan && (
        <PlanActivationModal
          plan={choosingPlan}
          billing={billing}
          onActivate={activate}
          onClose={() => setChoosingPlan(null)}
        />
      )}

      {changingPlan && (
        <PlanChangeModal
          plan={plan}
          usage={usage}
          catalog={catalog}
          billing={billing}
          quoteUpgrade={quoteUpgrade}
          onUpgrade={changePlan}
          onSetRenewalPlan={setRenewalPlan}
          onClearRenewalPlan={clearRenewalPlan}
          onClose={() => setChangingPlan(false)}
        />
      )}

      {payingRenewal && (
        <PaymentOtpModal
          title='Pay your renewal'
          payLabel={`Verify and pay ${plan.renewalTotal != null ? `₹${Number(plan.renewalTotal).toLocaleString('en-IN')}` : ''}`}
          summary={
            <Typography sx={bodySx}>
              Renews your {plan.nextPlanName || usage.plan?.name || 'Direct'} plan
              {plan.renewalTotal != null ? ` for ₹${Number(plan.renewalTotal).toLocaleString('en-IN')} (including GST)` : ''}, charged
              to your {plan.paidFrom === 'POSTPAID' ? 'credit line' : 'wallet'} now.
              {plan.status === 'SUSPENDED' ? ' Shipping on your own couriers resumes straight away.' : ''}
            </Typography>
          }
          onPay={renewNow}
          onClose={() => setPayingRenewal(false)}
        />
      )}

      {logOpen && (
        <ActivityLogModal
          entity={ACTIVITY_ENTITY.PLAN}
          title='Plan activity'
          onClose={() => setLogOpen(false)}
        />
      )}

      <ConfirmDialog
        title='Switch off all ProShip couriers?'
        open={confirmDisableProship}
        setOpen={setConfirmDisableProship}
        onConfirm={() => availability.setRowsEnabled(proshipRows, false)}
      >
        <Typography sx={bodySx}>
          All {proshipRows.length} ProShip couriers stop being allocated. Only your own couriers
          will carry orders, so <strong>your delivery coverage may shrink</strong> — a pincode none
          of your contracts reach can no longer be served.
          {enabledDirect === 0
            ? ' You currently have no Direct courier enabled, so nothing would be left to allocate to and order creation will fail.'
            : ` You have ${enabledDirect} Direct ${enabledDirect === 1 ? 'courier' : 'couriers'} enabled to carry everything.`}
          {' '}You can switch them back on here or in Couriers at any time.
        </Typography>
      </ConfirmDialog>

      <ConfirmDialog
        title='Deactivate ProShip Direct?'
        open={confirmDeactivate}
        setOpen={setConfirmDeactivate}
        onConfirm={deactivate}
      >
        <Typography sx={bodySx}>
          {enabledDirect > 0
            ? `${enabledDirect} Direct ${enabledDirect === 1 ? 'courier stops' : 'couriers stop'} being allocated from the next order. `
            : ''}
          Your accounts, uploads and rules are kept. Shipments already in transit are unaffected.
          {plan.autoRenew ? ' Auto-renew is cancelled with it.' : ''}
        </Typography>
      </ConfirmDialog>
    </Box>
  );
}

export default withLayout(DirectPlanHome);
